import { supabase } from './supabase';

/**
 * Supabase client wrapper with automatic operation tracking
 * Intercepts all database operations and logs them to the sync status panel
 */

let trackOperationFn = null;

// Set the tracking function (called by SyncStatusProvider)
export const setTrackingFunction = (fn) => {
  trackOperationFn = fn;
};

// ---------- OPERATION QUEUE + RESUME HANDLER ----------
const operationQueue = [];
let flushingQueue = false;

// helper to detect if page is visible
// DISABLED: Always return false so operations execute immediately regardless of tab visibility
const isPageHidden = () => false; // (typeof document !== 'undefined' && document.visibilityState === 'hidden');

// helper to run one queued operation
const runQueuedOp = async (queued) => {
  const { type, table, action, execFn, resolve, reject } = queued;
  try {
    const res = await execFn();
    resolve(res);
  } catch (err) {
    reject(err);
  }
};

// flush queue when visible
const flushQueue = async () => {
  if (flushingQueue || operationQueue.length === 0) return;
  flushingQueue = true;

  console.log(`[Queue] Flushing ${operationQueue.length} queued operations...`);

  // ensure we still have a valid in-memory session before flushing
  try {
    if (typeof window !== 'undefined' && window.tryReconnectIfSession) {
      const maybeClient = await window.tryReconnectIfSession(); // will redirect to login if session gone
      // if redirected, this code may never run further
    }
  } catch (e) {
    console.warn('[Queue] tryReconnectIfSession failed - dropping queue', e);
    // tryReconnectIfSession already handles redirect; drop queue
    operationQueue.length = 0;
    flushingQueue = false;
    return;
  }

  // run queued operations sequentially to avoid race and to limit concurrent suspended requests
  while (operationQueue.length > 0) {
    const queued = operationQueue.shift();
    try {
      await runQueuedOp(queued);
    } catch (err) {
      // if we hit unauthorized or abort, stop flushing to let UI handle redirect
      if (err?.status === 401 || err?.name === 'AbortError' || String(err).toLowerCase().includes('unauthorized')) {
        console.warn('[Queue] Stopping flush due to auth/abort error', err);
        // push the failing op back to head so user can retry after login
        operationQueue.unshift(queued);
        break;
      } else {
        console.warn('[Queue] operation failed while flushing — continuing with next', err);
        // swallow and continue
      }
    }
  }

  console.log(`[Queue] Flush complete. ${operationQueue.length} operations remaining.`);
  flushingQueue = false;
};

// visibility listener to flush on visible
// DISABLED: We now execute operations immediately regardless of tab visibility
// if (typeof window !== 'undefined') {
//   window.addEventListener('visibilitychange', () => {
//     if (document.visibilityState === 'visible') {
//       console.info('🔄 Page became visible - flushing queued sync operations');
//       flushQueue();
//     } else {
//       console.info('⏸️ Page hidden - queueing sync operations until visible');
//     }
//   });

//   // if network comes back, also flush
//   window.addEventListener('online', () => {
//     console.info('🔄 Network online - attempting to flush queue');
//     flushQueue();
//   });
// }

// ---------- resilient track() that queues while page is hidden ----------
const track = async (type, table, action, promiseOrFn) => {
  // promiseOrFn can be either a Promise or a function returning a Promise
  const execFn = typeof promiseOrFn === 'function' ? promiseOrFn : () => promiseOrFn;

  // Wrap execFn to use trackOperationFn if available
  const wrappedExecFn = async () => {
    if (trackOperationFn) {
      return await trackOperationFn(type, table, action, execFn());
    } else {
      return await execFn();
    }
  };

  // if page is hidden, enqueue the operation and return a promise that resolves later
  if (isPageHidden()) {
    console.info(`[Track] Page hidden - enqueueing ${action} for table ${table}`);
    return new Promise((resolve, reject) => {
      operationQueue.push({ type, table, action, execFn: wrappedExecFn, resolve, reject });
    });
  }

  // If page is visible, just run it but still handle aborts and 401s explicitly
  try {
    // attempt execution
    const res = await wrappedExecFn();
    return res;
  } catch (err) {
    // handle unauthorized: force redirect to login via tryReconnectIfSession
    if (err?.status === 401 || String(err).toLowerCase().includes('unauthorized')) {
      console.warn(`[Track] Unauthorized during ${action} — redirecting to login`);
      if (typeof window !== 'undefined' && window.tryReconnectIfSession) {
        await window.tryReconnectIfSession(); // will redirect to login if no session
      } else if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw err;
    }

    // if aborted by browser, don't silently retry here; queue it for later flush instead
    if (err?.name === 'AbortError') {
      console.warn(`[Track] Aborted during ${action} — enqueueing for later flush`);
      return new Promise((resolve, reject) => {
        operationQueue.push({ type, table, action, execFn: wrappedExecFn, resolve, reject });
      });
    }

    // other errors propagate as before
    throw err;
  }
};

/**
 * Wrapped Supabase client that tracks all operations
 */
export const supabaseTracked = {
  from: (table) => {
    const originalFrom = supabase.from(table);

    return {
      // SELECT operations
      select: (...args) => {
        const query = originalFrom.select(...args);

        // Wrap execute methods
        const originalMethods = {};

        // Store original methods
        ['single', 'maybeSingle', 'then', 'eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'like', 'ilike', 'is', 'in', 'contains', 'containedBy', 'rangeLt', 'rangeGt', 'rangeGte', 'rangeLte', 'rangeAdjacent', 'overlaps', 'textSearch', 'match', 'not', 'or', 'filter', 'order', 'limit', 'range'].forEach(method => {
          if (query[method]) {
            originalMethods[method] = query[method].bind(query);
            query[method] = (...methodArgs) => {
              const result = originalMethods[method](...methodArgs);
              // Return the query for chaining
              return result === query ? query : result;
            };
          }
        });

        // Wrap the final execution (then)
        const originalThen = query.then.bind(query);
        query.then = (onFulfilled, onRejected) => {
          const execFn = () => new Promise((resolve, reject) => {
            originalThen(resolve, reject);
          });
          const trackedPromise = track('fetch', table, `Fetching from ${table}`, execFn);
          return trackedPromise.then(onFulfilled, onRejected);
        };

        return query;
      },

      // INSERT operations
      insert: (data) => {
        const query = originalFrom.insert(data);
        const originalThen = query.then.bind(query);

        const count = Array.isArray(data) ? data.length : 1;
        const action = `Inserting ${count} record(s) into ${table}`;

        query.then = (onFulfilled, onRejected) => {
          const execFn = () => new Promise((resolve, reject) => {
            originalThen(resolve, reject);
          });
          const trackedPromise = track('insert', table, action, execFn);
          return trackedPromise.then(onFulfilled, onRejected);
        };

        // Support .select() chaining
        const originalSelect = query.select.bind(query);
        query.select = (...args) => {
          const selectQuery = originalSelect(...args);
          const selectThen = selectQuery.then.bind(selectQuery);

          selectQuery.then = (onFulfilled, onRejected) => {
            const execFn = () => new Promise((resolve, reject) => {
              selectThen(resolve, reject);
            });
            const trackedPromise = track('insert', table, action, execFn);
            return trackedPromise.then(onFulfilled, onRejected);
          };

          return selectQuery;
        };

        return query;
      },

      // UPDATE operations
      update: (data) => {
        const query = originalFrom.update(data);
        const action = `Updating record(s) in ${table}`;

        // Wrap eq, match, etc. for chaining
        const wrapQuery = (q) => {
          const originalThen = q.then.bind(q);
          q.then = (onFulfilled, onRejected) => {
            const execFn = () => new Promise((resolve, reject) => {
              originalThen(resolve, reject);
            });
            const trackedPromise = track('update', table, action, execFn);
            return trackedPromise.then(onFulfilled, onRejected);
          };

          // Support chaining
          ['eq', 'neq', 'select', 'single', 'maybeSingle'].forEach(method => {
            if (q[method]) {
              const originalMethod = q[method].bind(q);
              q[method] = (...args) => {
                const result = originalMethod(...args);
                return wrapQuery(result);
              };
            }
          });

          return q;
        };

        return wrapQuery(query);
      },

      // DELETE operations
      delete: () => {
        const query = originalFrom.delete();
        const action = `Deleting record(s) from ${table}`;

        const wrapQuery = (q) => {
          const originalThen = q.then.bind(q);
          q.then = (onFulfilled, onRejected) => {
            const execFn = () => new Promise((resolve, reject) => {
              originalThen(resolve, reject);
            });
            const trackedPromise = track('delete', table, action, execFn);
            return trackedPromise.then(onFulfilled, onRejected);
          };

          // Support chaining
          ['eq', 'neq', 'match'].forEach(method => {
            if (q[method]) {
              const originalMethod = q[method].bind(q);
              q[method] = (...args) => {
                const result = originalMethod(...args);
                return wrapQuery(result);
              };
            }
          });

          return q;
        };

        return wrapQuery(query);
      },

      // UPSERT operations
      upsert: (data, options) => {
        const query = originalFrom.upsert(data, options);
        const count = Array.isArray(data) ? data.length : 1;
        const action = `Upserting ${count} record(s) in ${table}`;

        const originalThen = query.then.bind(query);
        query.then = (onFulfilled, onRejected) => {
          const execFn = () => new Promise((resolve, reject) => {
            originalThen(resolve, reject);
          });
          const trackedPromise = track('upsert', table, action, execFn);
          return trackedPromise.then(onFulfilled, onRejected);
        };

        // Support .select() chaining
        if (query.select) {
          const originalSelect = query.select.bind(query);
          query.select = (...args) => {
            console.log(`[Track] upsert().select() called for ${table}`);
            const selectQuery = originalSelect(...args);
            const selectThen = selectQuery.then.bind(selectQuery);

            selectQuery.then = (onFulfilled, onRejected) => {
              console.log(`[Track] upsert().select().then() called for ${table}`);

              const execFn = () => new Promise((resolve, reject) => {
                console.log(`[Track] Executing query for ${table}...`);
                console.log(`[Track] About to call selectThen for ${table}`);
                console.log(`[Track] Page visibility:`, document.visibilityState);

                const selectThenResult = selectThen(
                  (result) => {
                    console.log(`[Track] Query resolved for ${table}`);
                    resolve(result);
                  },
                  (error) => {
                    console.log(`[Track] Query rejected for ${table}:`, error);
                    reject(error);
                  }
                );

                console.log(`[Track] selectThen returned:`, selectThenResult);
              });

              const trackedPromise = track('upsert', table, action, execFn);
              return trackedPromise.then(onFulfilled, onRejected);
            };

            return selectQuery;
          };
        }

        return query;
      }
    };
  },

  // Pass through auth methods
  auth: supabase.auth,

  // Pass through storage methods
  storage: supabase.storage,

  // Pass through other methods
  rpc: (...args) => supabase.rpc(...args),
  channel: (...args) => supabase.channel(...args),
  removeChannel: (...args) => supabase.removeChannel(...args),
  removeAllChannels: (...args) => supabase.removeAllChannels(...args),
  getChannels: (...args) => supabase.getChannels(...args),
  supabaseUrl: supabase.supabaseUrl,
  supabaseKey: supabase.supabaseKey
};


